<script setup lang="ts">
import { TcButton } from '@overseas/tcdesign'
import { ArrowRight } from '@element-plus/icons-vue'
import { loadScript } from '@overseas/utool'
import Cookies from 'js-cookie'
import type { CustomerPhoneItem } from './customerPhoneAdapter'
import { openPcLogin } from '@/utils/pcLogin'
import { customersEvent } from '~/utils/track/customer'

const props = defineProps<{
  callInfo: CustomerPhoneItem[]
  userInfo?: Record<string, any>
  showOtherRegions?: boolean
}>()
const emit = defineEmits<{ other: [] }>()
const showModal = defineModel<boolean>()
const { t, locale } = useI18n()
const sectoken = ref('')

const enableVoip = ref(false)
onMounted(() => {
  if (typeof window === 'undefined') { return }
  sectoken.value = Cookies.get('sectoken') || ''
  if ((window as any)._hopegooVoipSDK) {
    enableVoip.value = true
  }
  else {
    const runtimeConfig = useRuntimeConfig()
    const env = runtimeConfig.public.envData.VITE_PACK_ENV
    const joiner = env !== 'prod' ? '.t' : ''
    loadScript(`https://voip${joiner}.hopegoo.com/voip_sdk.js?0718`).then(() => {
      enableVoip.value = true
    })
  }
})

function getIcon(item: any, index: number) {
  if (item.icon) { return item.icon }
  const prefix = 'https://file.reshg.com/hopegoo/web-pc/images/customer'
  if (+item.configType === 1) { return `${prefix}/voip.png` }
  return `${prefix}/${index === 0 ? 'china_mainland.png' : 'global.png'}`
}

function toLogin() {
  openPcLogin()
}

function handleContainerClick() {
  showModal.value = false
  customersEvent.callFloatClickEvent('Close')
}

function handleVOIPCall(info: any) {
  customersEvent.callFloatClickEvent('voip')
  if (!Cookies.get('sectoken')) {
    toLogin()
    return
  }
  if (info.configType !== 1) {
    console.warn('Invalid config type for VOIP call')
    return
  }
  if (!(window as any)._hopegooVoipSDK) {
    console.error('VoIP SDK is not initialized')
    return
  }
  showModal.value = false
  const userInfo = props.userInfo || {}
  const userPhone = [userInfo?.contactPhoneAreaCode, userInfo?.contactPhoneOriginal]
    .filter(Boolean)
    .join('')

  ;(window as any)._hopegooVoipSDK?.open({
    phone: userPhone || '',
    sceneType: 0,
    lang: locale.value,
    from: 3,
    // source: 1
  })
}

function handleCall(info: any) {
  if (+info.configType === 1) {
    handleVOIPCall(info)
    return
  }
  const phone = info.dialPhone || info.phone
  if (phone) { window.location.href = `tel:${phone}` }
  customersEvent.callFloatClickEvent('Call')
}

function handleOtherRegions() {
  emit('other')
}
</script>

<template>
  <div v-if="showModal" class="online-service-popup callservice0910" @click="handleContainerClick">
    <div class="online-service-container" @click.stop="() => {}">
      <button
        type="button"
        class="close-btn"
        :aria-label="t('customer.p_customer_phone_close')"
        @click="handleContainerClick"
      >
        <img src="https://file.reshg.com/hopegoo/web-pc/images/icon_nav_common_close_line.svg" alt="">
      </button>
      <p class="online-title">
        {{ t("customer.p_customer_card_title_phone") }}
      </p>
      <div class="online-service-content">
        <template v-for="(info, index) in callInfo" :key="`${info.title}-${info.phone || ''}`">
          <div class="help-item">
            <img class="help-item-img" alt="" :src="getIcon(info, index)">
            <div class="help-info-warpper">
              <p class="help-item-title">
                {{ info.title }}
              </p>
              <p v-if="info.phone" class="help-item-phone">
                {{ info.phone }}
              </p>
              <p v-if="info.desc" class="help-item-desc">
                {{ info.desc }}
              </p>
            </div>
            <TcButton
              v-if="+(info.configType) === 1"
              class="help-item-btn ml-auto my-auto"
              size="small"
              color="#00C868"
              plain
              round
              :disabled="+(info.configType) === 1 && !enableVoip"
              @click="handleCall(info)"
            >
              {{ info.buttonDesc }}
            </TcButton>
          </div>
          <div v-if="callInfo.length - 1 !== index" class="divider" />
        </template>
        <button v-if="showOtherRegions" type="button" class="other-region-entry" @click="handleOtherRegions">
          <span>{{ t('customer.p_customer_phone_other_regions') }}</span>
          <el-icon aria-hidden="true">
            <ArrowRight />
          </el-icon>
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.ml-auto {
  margin-left: auto;
}
.my-auto {
  margin-top: auto;
  margin-bottom: auto;
}
.online-service-popup.callservice0910 {
    position: fixed;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.3);
    z-index: 9999;

    display: flex;
    justify-content: center;
    align-items: center;

    .close-btn {
      display: flex;
      width: 40px;
      height: 40px;
      align-items: center;
      justify-content: center;
      position: absolute;
      top: 18px;
      right: 14px;
      padding: 0;
      background: transparent;
      border: 0;
      cursor: pointer;

      img {
        width: 20px;
        height: 20px;
      }
    }

    .online-service-container {
      display: flex;
      width: clamp(600px, 40vw, 800px);
      max-width: calc(100vw - 48px);
      max-height: 70vh;
      flex-direction: column;
      gap: 24px;
      position: relative;
      border-radius: 16px;
      padding: 24px;
      box-sizing: border-box;
      overflow: hidden;

      background-color: #fff;
      box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.08);

      .online-title {
        flex: none;
        color: #000;
        font-size: 24px;
        font-weight: 700;
        line-height: 1.2;
      }
    }

    .online-service-content {
      display: flex;
      min-height: 0;
      flex-direction: column;
      gap: 24px;
      overflow-y: auto;
      overscroll-behavior: contain;
    }

    .divider {
      flex: none;
      width: 100%;
      height: 0;
      border-top: 0.5px solid #e4e4e4;
    }
    .other-region-entry {
      display: flex;
      width: 100%;
      height: 24px;
      align-items: center;
      justify-content: center;
      gap: 0;
      padding: 0;
      color: #000;
      font-size: 16px;
      line-height: 24px;
      background: transparent;
      border: 0;
      cursor: pointer;

      &:hover {
        color: var(--Brand-Green);
      }

      .el-icon {
        width: 24px;
        height: 24px;
        font-size: 20px;
      }
    }
    .help-item {
      display: flex;
      align-items: center;
      width: 100%;

      .help-item-img {
        width: 32px;
        height: 32px;
        margin-right: 20px;
        object-fit: contain;
      }
      .help-info-warpper {
        min-width: 0;
        flex: 1;
        overflow-wrap: anywhere;
        word-break: break-word;
      }
      .help-item-title {
        font-size: 18px;
        line-height: 24px;
        color: #000;
      }
      .help-item-phone {
        margin-top: 4px;
        font-size: 18px;
        font-weight: 700;
        line-height: 24px;
        color: #000;
      }
      .help-item-desc {
        margin-top: 4px;
        color: #999;
        font-size: 14px;
        font-weight: 400;
        line-height: 1.6;
      }

      .help-item-btn {
        height: 40px;
        min-height: 40px;
        min-width: 76px;
        max-width: 30%;
        flex: 0 1 auto;
        margin-left: 24px;
        padding: 8px 24px;
        font-size: 16px;
        font-weight: 700;
        line-height: 24px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        > span {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }
</style>
